import { WORDPRESS_CONFIG } from '@/config/wordpress';
import { apiUrl } from '@/lib/apiBase';
import { normalizeArticleImageUrl } from '@/lib/articleImage';
export interface WordPressPost {
    id: number;
    slug: string;
    date: string;
    title: {
        rendered: string;
    };
    excerpt: {
        rendered: string;
    };
    content: {
        rendered: string;
    };
    link: string;
    author: number;
    featured_media: number;
    categories: number[];
    _embedded?: {
        author?: Array<{
            name: string;
        }>;
        'wp:featuredmedia'?: Array<{
            source_url: string;
            alt_text: string;
        }>;
        'wp:term'?: Array<Array<{
            name: string;
            taxonomy: string;
        }>>;
    };
}
export interface WordPressArticle {
    id: number;
    slug: string;
    title: string;
    excerpt: string;
    content: string;
    author: string;
    readTime: string;
    category: string;
    categories?: string[];
    tags?: string[];
    image: string;
    date: string;
    link: string;
}
export interface WordPressComment {
    id: number;
    post: number;
    parent?: number;
    author_name: string;
    date: string;
    content: {
        rendered: string;
    };
    author_avatar_urls?: Record<string, string>;
}
class WordPressService {
    private baseURL: string;
    private proxyURL: string;
    private perPage: number = WORDPRESS_CONFIG.DEFAULT_PER_PAGE;
    constructor(baseURL: string) {
        this.baseURL = baseURL.replace(/\/$/, '');
        this.proxyURL = apiUrl('/api/wp-proxy.php');
    }
    private encodeBase64(value: string): string {
        if (typeof btoa === 'function') {
            return btoa(value);
        }
        const BufferCtor = (globalThis as { Buffer?: { from: (input: string, encoding: string) => { toString: (enc: string) => string } } }).Buffer;
        if (BufferCtor) {
            return BufferCtor.from(value, 'utf8').toString('base64');
        }
        throw new Error('Base64 encoder is not available');
    }
    private buildProxyUrl(targetUrl: string): string {
        const encoded = encodeURIComponent(this.encodeBase64(targetUrl));
        return `${this.proxyURL}?u=${encoded}`;
    }
    private normalizeMediaUrls(value: string): string {
        if (!value) {
            return value;
        }
        const normalizeUrl = (url: string) => {
            if (!url) {
                return url;
            }
            const trimmed = url.trim();
            if (!trimmed) {
                return trimmed;
            }
            const normalizedScheme = trimmed.replace(/^http:\/\//i, 'https://').replace(/^\/\//, 'https://');
            if (/^https?:\/\//i.test(normalizedScheme)) {
                return normalizedScheme
                    .replace(/https?:\/\/cms\.salamatlab\.com/i, 'https://cms.salamatlab.com')
                    .replace(/https?:\/\/www\.salamatlab\.com/i, 'https://www.salamatlab.com')
                    .replace(/https?:\/\/salamatlab\.com/i, 'https://salamatlab.com');
            }
            if (/^\/(?:wp-content|wp-includes|uploads)\//i.test(normalizedScheme)) {
                return `${WORDPRESS_CONFIG.SITE_URL.replace(/\/+$/, '')}${normalizedScheme}`;
            }
            return normalizedScheme;
        };
        return value
            .replace(/&amp;/g, '&')
            .replace(/(https?:\/\/[^\s"'>]+)/g, (match) => normalizeUrl(match))
            .replace(/(?<!:)(\/\/[^\s"'>]+)/g, (match) => normalizeUrl(match))
            .replace(/(["'])(\/(?:wp-content|wp-includes|uploads)\/[^"'>\s]+)\1/g, (match, quote, url) => `${quote}${normalizeUrl(url)}${quote}`)
            .replace(/srcset=(["'])([^"']+)\1/g, (_match, quote, value) => {
            const normalized = value
                .split(',')
                .map((source) => {
                    const [src, descriptor] = source.trim().split(/\s+/);
                    if (!src) {
                        return source;
                    }
                    return [normalizeUrl(src), descriptor].filter(Boolean).join(' ');
                })
                .join(', ');
            return `srcset=${quote}${normalized}${quote}`;
        });
    }
    private getPostsEndpoint(params: Record<string, string | number> = {}): string {
        const searchParams = new URLSearchParams();
        searchParams.append('_embed', '1');
        searchParams.append('per_page', this.perPage.toString());
        searchParams.append('orderby', 'date');
        searchParams.append('order', 'desc');
        searchParams.append('status', 'publish');
        Object.entries(params).forEach(([key, value]) => {
            searchParams.append(key, value.toString());
        });
        const directUrl = `${this.baseURL}/wp-json/wp/v2/posts?${searchParams.toString()}`;
        return this.buildProxyUrl(directUrl);
    }
    private async wait(ms: number): Promise<void> {
        await new Promise(resolve => globalThis.setTimeout(resolve, ms));
    }
    private isRetryableError(error: unknown): boolean {
        if (!(error instanceof Error)) {
            return false;
        }
        const message = error.message.toLowerCase();
        return message.includes('timeout')
            || message.includes('network')
            || message.includes('failed to fetch')
            || message.includes('http error! status: 502')
            || message.includes('http error! status: 503')
            || message.includes('http error! status: 504');
    }
    private async fetchJson<T>(url: string): Promise<T> {
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            const controller = new AbortController();
            const timeoutId = globalThis.setTimeout(() => controller.abort(), WORDPRESS_CONFIG.REQUEST_TIMEOUT_MS);
            try {
                const response = await fetch(url, {
                    signal: controller.signal,
                    headers: {
                        Accept: 'application/json'
                    }
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return await response.json() as T;
            }
            catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    console.error('WordPress request timed out', {
                        url,
                        timeoutMs: WORDPRESS_CONFIG.REQUEST_TIMEOUT_MS,
                        attempt
                    });
                    error = new Error('Request timeout');
                } else {
                    console.error('WordPress request failed', { url, error, attempt });
                }
                const canRetry = attempt < maxAttempts && this.isRetryableError(error);
                if (!canRetry) {
                    throw error;
                }
                await this.wait(300 * attempt);
            }
            finally {
                globalThis.clearTimeout(timeoutId);
            }
        }
        throw new Error('Request failed after retries');
    }
    private async fetchJsonWithMeta<T>(url: string): Promise<{
        data: T;
        headers: Headers;
    }> {
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            const controller = new AbortController();
            const timeoutId = globalThis.setTimeout(() => controller.abort(), WORDPRESS_CONFIG.REQUEST_TIMEOUT_MS);
            try {
                const response = await fetch(url, {
                    signal: controller.signal,
                    headers: {
                        Accept: 'application/json'
                    }
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json() as T;
                return { data, headers: response.headers };
            }
            catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    console.error('WordPress request with meta timed out', {
                        url,
                        timeoutMs: WORDPRESS_CONFIG.REQUEST_TIMEOUT_MS,
                        attempt
                    });
                    error = new Error('Request timeout');
                } else {
                    console.error('WordPress request with meta failed', { url, error, attempt });
                }
                const canRetry = attempt < maxAttempts && this.isRetryableError(error);
                if (!canRetry) {
                    throw error;
                }
                await this.wait(300 * attempt);
            }
            finally {
                globalThis.clearTimeout(timeoutId);
            }
        }
        throw new Error('Request failed after retries');
    }
    async fetchPosts(params: Record<string, string | number> = {}): Promise<WordPressPost[]> {
        try {
            const posts = await this.fetchJson<WordPressPost[]>(this.getPostsEndpoint(params));
            return posts;
        }
        catch (error) {
            console.error('Error fetching WordPress posts:', error);
            throw error;
        }
    }
    async fetchPostsWithMeta(params: Record<string, string | number> = {}): Promise<{
        posts: WordPressPost[];
        totalPosts: number;
        totalPages: number;
    }> {
        try {
            const { data: posts, headers } = await this.fetchJsonWithMeta<WordPressPost[]>(this.getPostsEndpoint(params));
            const totalPostsHeader = parseInt(headers.get('X-WP-Total') || '0', 10);
            const totalPagesHeader = parseInt(headers.get('X-WP-TotalPages') || '0', 10);
            const perPage = Number(params.per_page) || this.perPage;
            const totalPosts = Number.isFinite(totalPostsHeader) && totalPostsHeader > 0 ? totalPostsHeader : posts.length;
            const totalPages = Number.isFinite(totalPagesHeader) && totalPagesHeader > 0
                ? totalPagesHeader
                : Math.max(1, Math.ceil(totalPosts / perPage));
            return { posts, totalPosts, totalPages };
        }
        catch (error) {
            console.error('Error fetching WordPress posts with meta:', error);
            return { posts: [], totalPosts: 0, totalPages: 0 };
        }
    }
    async fetchPost(id: number): Promise<WordPressPost> {
        try {
            const directUrl = `${this.baseURL}/wp-json/wp/v2/posts/${id}?_embed=1`;
            const post = await this.fetchJson<WordPressPost>(this.buildProxyUrl(directUrl));
            return post;
        }
        catch (error) {
            console.error('Error fetching WordPress post:', error);
            throw error;
        }
    }
    async fetchPostBySlug(slug: string): Promise<WordPressPost> {
        try {
            const directUrl = `${this.baseURL}/wp-json/wp/v2/posts?slug=${encodeURIComponent(slug)}&_embed=1&status=publish&per_page=1`;
            const posts = await this.fetchJson<WordPressPost[]>(this.buildProxyUrl(directUrl));
            if (!posts.length) {
                throw new Error('Post not found');
            }
            return posts[0];
        }
        catch (error) {
            console.error('Error fetching WordPress post by slug:', error);
            throw error;
        }
    }
    convertToArticle(post: WordPressPost): WordPressArticle {
        const authorName = post._embedded?.author?.[0]?.name || 'نویسنده ناشناس';
        const featuredImage = normalizeArticleImageUrl(this.normalizeMediaUrls(post._embedded?.['wp:featuredmedia']?.[0]?.source_url || WORDPRESS_CONFIG.DEFAULT_IMAGE));
        const embeddedTerms = post._embedded?.['wp:term'] || [];
        const embeddedCategories = (embeddedTerms[0] || []).filter(t => t.taxonomy === 'category');
        const embeddedTags = (embeddedTerms[1] || []).filter(t => t.taxonomy === 'post_tag');
        const categories = embeddedCategories.map((t) => t.name);
        const tags = embeddedTags.map((t) => t.name);
        const category = categories[0] || 'عمومی';
        const wordCount = post.content.rendered.replace(/<[^>]*>/g, '').split(' ').length;
        const readTimeMinutes = Math.ceil(wordCount / 200);
        const readTime = `${readTimeMinutes} دقیقه`;
        const postDate = new Date(post.date);
        const formattedDate = postDate.toLocaleDateString('fa-IR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
        return {
            id: post.id,
            slug: post.slug,
            title: post.title.rendered,
            excerpt: post.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 150) + '...',
            content: this.normalizeMediaUrls(post.content.rendered),
            author: authorName,
            readTime,
            category,
            categories,
            tags,
            image: featuredImage,
            date: formattedDate,
            link: post.link
        };
    }
    async getFeaturedArticles(count: number = WORDPRESS_CONFIG.FEATURED_ARTICLES_COUNT): Promise<WordPressArticle[]> {
        try {
            const posts = await this.fetchPosts({ per_page: count });
            return posts.map(post => this.convertToArticle(post));
        }
        catch (error) {
            console.error('Error fetching featured articles:', error);
            return [];
        }
    }
    async fetchComments(postId: number, perPage: number = 100): Promise<WordPressComment[]> {
        try {
            const url = `${this.baseURL}/wp-json/wp/v2/comments?post=${postId}&per_page=${perPage}`;
            const comments = await this.fetchJson<WordPressComment[]>(this.buildProxyUrl(url));
            return comments;
        }
        catch (error) {
            console.error('Error fetching WordPress comments:', error);
            return [];
        }
    }

    async submitComment(commentData: {
        post: number;
        author_name: string;
        author_email: string;
        content: string;
    }): Promise<WordPressComment> {
        try {
            const url = `${this.baseURL}/wp-json/wp/v2/comments`;
            
            const controller = new AbortController();
            const timeoutId = globalThis.setTimeout(() => controller.abort(), WORDPRESS_CONFIG.REQUEST_TIMEOUT_MS);
            
            try {
                const response = await fetch(this.buildProxyUrl(url), {
                    method: 'POST',
                    signal: controller.signal,
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(commentData)
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to submit comment: ${response.status}`);
                }
                
                return await response.json() as WordPressComment;
            } finally {
                globalThis.clearTimeout(timeoutId);
            }
        } catch (error) {
            console.error('Error submitting comment:', error);
            throw error;
        }
    }
    async getRelatedArticles(post: WordPressPost, count: number = 3): Promise<WordPressArticle[]> {
        try {
            const categoryIds = (post.categories || []).slice(0, 3);
            const params: Record<string, string | number> = {
                per_page: count,
                _embed: 1,
                exclude: post.id,
                order: 'desc',
                orderby: 'date',
            };
            if (categoryIds.length > 0) {
                params.categories = categoryIds.join(',');
            }
            const relatedUrl = `${this.baseURL}/wp-json/wp/v2/posts?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()}`;
            const posts = await this.fetchJson<WordPressPost[]>(this.buildProxyUrl(relatedUrl));
            return posts.map((p) => this.convertToArticle(p));
        }
        catch (error) {
            console.error('Error fetching related articles:', error);
            return [];
        }
    }
    async getAllArticles(page: number = 1, perPage: number = WORDPRESS_CONFIG.DEFAULT_PER_PAGE): Promise<{
        articles: WordPressArticle[];
        totalPages: number;
        totalPosts: number;
    }> {
        try {
            const { posts, totalPages, totalPosts } = await this.fetchPostsWithMeta({
                per_page: perPage,
                page
            });
            const articles = posts.map(post => this.convertToArticle(post));
            return { articles, totalPages, totalPosts };
        }
        catch (error) {
            console.error('Error fetching all articles:', error);
            return {
                articles: [],
                totalPages: 0,
                totalPosts: 0
            };
        }
    }
    async searchArticles(searchTerm: string, page: number = 1): Promise<{
        articles: WordPressArticle[];
        totalPages: number;
        totalPosts: number;
    }> {
        try {
            const { posts, totalPages, totalPosts } = await this.fetchPostsWithMeta({
                search: searchTerm,
                per_page: this.perPage,
                page
            });
            const articles = posts.map(post => this.convertToArticle(post));
            return { articles, totalPages, totalPosts };
        }
        catch (error) {
            console.error('Error searching articles:', error);
            return {
                articles: [],
                totalPages: 0,
                totalPosts: 0
            };
        }
    }
}
export const wordpressService = new WordPressService(WORDPRESS_CONFIG.SITE_URL);
export default WordPressService;
